import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { AcademyRichText } from '../academy/AcademyRichText';
import {
  saveSelfAssessment,
  startSelfAssessment,
  selfAssessmentKey,
  type AssessmentDraftQuestion,
  type SelfAssessmentPaper,
} from './studySelfAssessmentApi';

const field =
  'mt-1 min-h-11 w-full rounded-xl border border-hairline bg-canvas-pure p-3 text-sm text-ink';
export function StudySelfAssessmentDraft({ paper }: { paper: SelfAssessmentPaper }) {
  const client = useQueryClient();
  const form = useForm<{ title: string; questions: AssessmentDraftQuestion[] }>({
    defaultValues: { title: paper.assessment.title, questions: paper.draft_questions ?? [] },
  });
  const saved = (value: SelfAssessmentPaper) => {
    client.setQueryData(selfAssessmentKey(paper.analysis_id, paper.id), value);
    form.reset({ title: value.assessment.title, questions: value.draft_questions ?? [] });
  };
  const save = useMutation({
    mutationFn: async () => {
      await form.handleSubmit(async (value) =>
        saved(await saveSelfAssessment(paper, value.title, value.questions)),
      )();
    },
  });
  const start = useMutation({ mutationFn: () => startSelfAssessment(paper), onSuccess: saved });
  const busy = save.isPending || start.isPending;
  return (
    <section aria-label="Assessment draft preview" className="mt-5 space-y-4">
      <h2 className="text-2xl font-extrabold">Preview and edit the assessment</h2>
      <p className="text-sm">
        The authoring preview includes the answer key. Check each question and its solution. Save
        changes before starting; starting locks this version and hides answers until you try.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-4"
      >
        <fieldset disabled={busy} className="space-y-4">
          <label className="block text-sm font-bold">
            Assessment title
            <input
              className={field}
              maxLength={160}
              {...form.register('title', { required: true })}
            />
          </label>
          {paper.draft_questions?.map((q, i) => (
            <article key={i} className="rounded-2xl border border-hairline bg-canvas-pure p-4">
              <h3 className="font-extrabold">
                Question {i + 1} · {q.kind.replace('_', ' ')}
              </h3>
              <label className="mt-3 block text-sm font-bold">
                Question text
                <textarea
                  className={field}
                  rows={3}
                  maxLength={800}
                  {...form.register(`questions.${i}.stem`, { required: true })}
                />
              </label>
              {q.kind === 'multiple_choice' && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {q.options?.map((_, j) => (
                    <label key={j} className="text-sm">
                      Option {'ABCD'[j]}
                      <input
                        className={field}
                        maxLength={240}
                        {...form.register(`questions.${i}.options.${j}`, { required: true })}
                      />
                    </label>
                  ))}
                  <label className="text-sm font-bold">
                    Correct option
                    <select className={field} {...form.register(`questions.${i}.correct_option`)}>
                      {['A', 'B', 'C', 'D'].map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              <label className="mt-3 block text-sm font-bold">
                Reference answer
                <input
                  className={field}
                  maxLength={400}
                  {...form.register(`questions.${i}.answer`, { required: true })}
                />
              </label>
              <label className="mt-3 block text-sm font-bold">
                Worked solution
                <textarea
                  className={field}
                  rows={4}
                  maxLength={1500}
                  {...form.register(`questions.${i}.worked_solution`, { required: true })}
                />
              </label>
              <details className="mt-3 text-sm">
                <summary className="min-h-11 cursor-pointer font-bold">
                  Rendered question preview
                </summary>
                <AcademyRichText text={form.watch(`questions.${i}.stem`)} />
              </details>
            </article>
          ))}
        </fieldset>
        {Object.keys(form.formState.errors).length > 0 && (
          <p role="alert">
            Complete the title, questions, options, reference answers and solutions.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className="btn-pill-secondary"
            type="submit"
            disabled={busy || !form.formState.isDirty}
          >
            {save.isPending ? 'Saving draft…' : 'Save draft changes'}
          </button>
          <button
            className="btn-pill-primary"
            type="button"
            disabled={busy || form.formState.isDirty}
            onClick={() => start.mutate()}
          >
            {start.isPending ? 'Starting…' : 'Start assessment'}
          </button>
        </div>
        {form.formState.isDirty && (
          <p role="status" className="mt-2 text-sm">
            Unsaved draft changes. Save before starting.
          </p>
        )}
        {(save.error || start.error) && (
          <p role="alert" className="mt-3 rounded-xl bg-wash-coral p-3">
            {(save.error ?? start.error)?.message} Your draft text is kept.
          </p>
        )}
      </form>
    </section>
  );
}
